// @greenlight-spec: 11-observability.md#cost-management
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { AppCost, CostSummary, TokenCostWindow } from '@greenlight/shared';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { DataList, type DataListColumn, type SortState } from '@/components/greenlight/DataList';
import { Dash } from '@/components/greenlight/Dash';
import { EmptyState } from '@/components/greenlight/EmptyState';
import { PageHeader } from '@/components/greenlight/PageHeader';
import { Sparkline } from '@/components/greenlight/Sparkline';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { useCurrentUser } from '@/lib/auth';
import { isPreviewFeatureEnabled } from '@/lib/preview-features';

const COST_KEY = ['cost-summary'];

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const QUANTITY = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const DATE = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

function usd(amount: number): string {
  return USD.format(amount);
}

function day(iso: string): string {
  return DATE.format(new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso));
}

const CARD_LABEL: Record<Exclude<CostSummary['rates']['source'], 'list'>, string> = {
  installation: 'your own rates',
  grandfathered: 'your grandfathered rates',
  marketplace_listing: 'the marketplace listing’s rates',
  policy: 'your account’s terms',
};

const FALLBACK_REASON: Record<NonNullable<CostSummary['rates']['fallback']>, string> = {
  not_enrolled: 'this install has not enrolled with Shift yet',
  unreachable: 'your rates could not be read from Shift just now',
  refused: 'Shift refused the request for your rates',
  malformed: 'the rates Shift sent could not be read',
  no_rates: 'Shift has no rates on file for this install',
};

/** One line naming the card that priced every figure on the page. */
export function rateLabel(rates: CostSummary['rates']): string {
  if (rates.source !== 'list') return `Priced at ${CARD_LABEL[rates.source]}.`;
  return rates.fallback
    ? `Priced at list rates because ${FALLBACK_REASON[rates.fallback]}.`
    : 'Priced at list rates.';
}

const CHARGE_LABEL: Record<CostSummary['install_charges'][number]['kind'], string> = {
  monthly_fee: 'Monthly fee',
  license: 'License',
};

function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="rounded-gl border border-border p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function LifetimeHint({
  lifetime,
}: {
  lifetime: { measured_since: string; incomplete: boolean };
}): React.JSX.Element {
  return lifetime.incomplete ? (
    <>
      <span className="font-medium text-foreground">Partial</span>: some months since{' '}
      {day(lifetime.measured_since)} were only partly measured
    </>
  ) : (
    <>Since {day(lifetime.measured_since)}</>
  );
}

function appLabel(row: AppCost): string {
  return row.name ?? row.slug;
}

function TokenWindow({ label, window }: { label: string; window: TokenCostWindow }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="tabular-nums">
        {usd(window.usd)}{' '}
        <span className="text-muted-foreground">
          · {window.sessions} {window.sessions === 1 ? 'session' : 'sessions'}
        </span>
      </p>
      {window.unpriced_tokens > 0 && (
        <p className="text-xs text-muted-foreground">
          {window.unpriced_tokens.toLocaleString('en-US')} tokens from models with no published
          price are not included
        </p>
      )}
    </div>
  );
}

function Breakdown({
  row,
  chargesLiveApps,
  id,
}: {
  row: AppCost;
  chargesLiveApps: boolean;
  id: string;
}): React.JSX.Element {
  const lines: { label: string; unit: string; key: 'app_hours' | 'deployments' | 'mau_per_app' }[] =
    [
      { label: 'App-hours', unit: 'hours', key: 'app_hours' },
      { label: 'Deployments', unit: 'deployments', key: 'deployments' },
      {
        label: 'Monthly active users, if the month closed today',
        unit: 'users',
        key: 'mau_per_app',
      },
    ];
  return (
    <div id={id} className="grid gap-4 py-2 text-sm lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <table className="w-full text-left">
        <caption className="sr-only">Charge by dimension for {appLabel(row)}</caption>
        <thead className="text-xs text-muted-foreground">
          <tr>
            <th scope="col" className="py-1 font-medium">
              Dimension
            </th>
            <th scope="col" className="py-1 text-right font-medium">
              Month to date
            </th>
            <th scope="col" className="py-1 text-right font-medium">
              Lifetime
            </th>
          </tr>
        </thead>
        <tbody className="tabular-nums">
          {lines.map(({ label, unit, key }) => {
            const month = row.month_to_date[key];
            const lifetime = row.lifetime?.dimensions[key];
            return (
              <tr key={key} className="border-t border-border/60">
                <th scope="row" className="py-1.5 pr-3 font-normal">
                  {label}
                </th>
                <td className="py-1.5 text-right">
                  {usd(month.usd)}
                  <span className="block text-xs text-muted-foreground">
                    {QUANTITY.format(month.usage)} {unit}
                  </span>
                </td>
                <td className="py-1.5 text-right">
                  {lifetime ? (
                    <>
                      {usd(lifetime.usd)}
                      <span className="block text-xs text-muted-foreground">
                        {QUANTITY.format(lifetime.usage)} {unit}
                      </span>
                    </>
                  ) : (
                    <Dash />
                  )}
                </td>
              </tr>
            );
          })}
          {chargesLiveApps && (
            <tr className="border-t border-border/60">
              <th scope="row" className="py-1.5 pr-3 font-normal">
                Live app
              </th>
              <td className="py-1.5 text-right">{usd(row.month_to_date.live_apps.usd)}</td>
              <td className="py-1.5 text-right">
                <Dash />
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="space-y-3">
        {row.trend.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground">Last 30 days, app-hours and deployments</p>
            <Sparkline
              data={row.trend.map((point) => point.usd)}
              ariaLabel={`30-day charge trend for ${appLabel(row)}`}
              filled
            />
          </div>
        )}
        {row.tokens && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Build tokens, API-equivalent
            </p>
            <TokenWindow label="This month" window={row.tokens.month_to_date} />
            <TokenWindow label="Lifetime" window={row.tokens.lifetime} />
          </div>
        )}
      </div>
    </div>
  );
}

type SortKey = 'app' | 'mtd' | 'projected' | 'lifetime' | 'tokens';

const SORT_VALUE: Record<SortKey, (row: AppCost) => number | string> = {
  app: (row) => appLabel(row).toLowerCase(),
  mtd: (row) => row.month_to_date_usd,
  projected: (row) => row.projected_month_usd ?? -1,
  lifetime: (row) => row.lifetime?.usd ?? -1,
  tokens: (row) => row.tokens?.lifetime.usd ?? -1,
};

function sortRows(rows: AppCost[], sort: SortState): AppCost[] {
  const value = SORT_VALUE[sort.key as SortKey];
  const sign = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    return (x < y ? -1 : x > y ? 1 : 0) * sign;
  });
}

function CostView({ cost }: { cost: CostSummary }): React.JSX.Element {
  const [sort, setSort] = React.useState<SortState>({ key: 'mtd', direction: 'desc' });
  const [expanded, setExpanded] = React.useState<ReadonlySet<string>>(new Set());

  const { total, tokens } = cost;
  const showTokens = tokens.reporting_enabled || cost.apps.some((row) => row.tokens !== null);
  const chargesLiveApps = cost.rates.usd_per_live_app !== null;
  const rows = sortRows(cost.apps, sort);

  const toggle = (appId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(appId)) next.delete(appId);
      else next.add(appId);
      return next;
    });

  const columns: DataListColumn<AppCost>[] = [
    {
      key: 'app',
      header: 'App',
      primary: true,
      sortable: true,
      render: (row) => (
        <span className="flex flex-wrap items-center gap-2">
          {row.deleted ? (
            <span className="text-muted-foreground">
              {row.slug} <span className="text-xs">(deleted)</span>
            </span>
          ) : (
            <Link to={`/apps/${row.app_id}`} className="font-medium hover:underline">
              {appLabel(row)}
            </Link>
          )}
          {row.idle && <StatusBadge variant="degraded">Idle</StatusBadge>}
        </span>
      ),
    },
    {
      key: 'mtd',
      header: 'Month to date',
      sortable: true,
      render: (row) => <span className="tabular-nums">{usd(row.month_to_date_usd)}</span>,
    },
    {
      key: 'projected',
      header: 'Projected',
      sortable: true,
      priority: 'lg',
      render: (row) =>
        row.projected_month_usd === null ? (
          <Dash />
        ) : (
          <span className="tabular-nums">{usd(row.projected_month_usd)}</span>
        ),
    },
    {
      key: 'lifetime',
      header: 'Lifetime',
      sortable: true,
      priority: 'md',
      render: (row) =>
        row.lifetime === null ? (
          <Dash />
        ) : (
          <span className="tabular-nums">
            {usd(row.lifetime.usd)}
            {row.lifetime.incomplete && (
              <span className="ml-1 text-xs text-muted-foreground">(partial)</span>
            )}
          </span>
        ),
    },
    ...(showTokens
      ? [
          {
            key: 'tokens',
            header: 'Build tokens',
            sortable: true,
            priority: 'lg',
            render: (row) =>
              row.tokens === null ? (
                <Dash />
              ) : (
                <span className="tabular-nums">{usd(row.tokens.lifetime.usd)}</span>
              ),
          } satisfies DataListColumn<AppCost>,
        ]
      : []),
  ];

  return (
    <>
      <div className="space-y-1">
        <p className="text-sm text-muted-foreground">
          {rateLabel(cost.rates)} Usage is measured by this install; these figures are not an
          invoice.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatTile label="Month to date" value={usd(total.month_to_date_usd)} hint="Measured" />
        <StatTile
          label="Projected month"
          value={total.projected_month_usd === null ? '—' : usd(total.projected_month_usd)}
          hint={
            total.projected_month_usd === null
              ? 'Estimate available from the 2nd of the month'
              : 'Estimate from the month so far'
          }
        />
        <StatTile
          label="Lifetime"
          value={usd(total.lifetime.usd)}
          hint={<LifetimeHint lifetime={total.lifetime} />}
        />
      </div>

      {cost.install_charges.length > 0 && (
        <ul aria-label="Install charges" className="space-y-1 text-sm">
          {cost.install_charges.map((charge, i) => (
            <li key={i} className="flex flex-wrap justify-between gap-x-3">
              <span>{CHARGE_LABEL[charge.kind]}</span>
              <span className="tabular-nums text-muted-foreground">
                {usd(charge.month_usd)} this month
              </span>
            </li>
          ))}
        </ul>
      )}

      {showTokens && (
        <p className="text-sm text-muted-foreground">
          Build tokens are API-equivalent: what builders’ coding sessions would cost at the vendor’s
          API prices, not money spent, and never part of the totals.
          {tokens.reporting_since && <> Reporting since {day(tokens.reporting_since)}.</>}
        </p>
      )}
      {!tokens.reporting_enabled && (
        <p className="text-sm text-muted-foreground">
          {showTokens
            ? 'Token reporting is off, so no new sessions are counted.'
            : 'Token cost per app is off.'}{' '}
          <Link to="/settings?tab=org" className="font-medium text-foreground underline">
            Turn on build token usage in Org settings
          </Link>
          .
        </p>
      )}

      {rows.length === 0 ? (
        <EmptyState
          title="No app usage yet"
          description="Apps appear here once they run, deploy, or have users."
        />
      ) : (
        <DataList
          items={rows}
          columns={columns}
          rowKey={(row) => row.app_id}
          ariaLabel="Greenlight charge per app"
          sort={sort}
          onSortChange={(key) =>
            setSort((prev) =>
              prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: key === 'app' ? 'asc' : 'desc' },
            )
          }
          rowClassName={(row) => (row.deleted ? 'text-muted-foreground' : undefined)}
          rowAction={(row, view) => {
            const open = expanded.has(row.app_id);
            const Icon = open ? ChevronDown : ChevronRight;
            return (
              <Button
                variant="ghost"
                size="sm"
                className="h-11 w-11 md:h-9 md:w-9"
                aria-expanded={open}
                aria-controls={`cost-breakdown-${view}-${row.app_id}`}
                aria-label={`${open ? 'Hide' : 'Show'} breakdown for ${appLabel(row)}`}
                onClick={() => toggle(row.app_id)}
              >
                <Icon className="size-4" aria-hidden />
              </Button>
            );
          }}
          renderExpanded={(row, view) =>
            expanded.has(row.app_id) ? (
              <Breakdown
                row={row}
                chargesLiveApps={chargesLiveApps}
                id={`cost-breakdown-${view}-${row.app_id}`}
              />
            ) : null
          }
        />
      )}
    </>
  );
}

export function CostPage(): React.JSX.Element {
  usePageTitle('Cost Management');
  const user = useCurrentUser();
  const previewEnabled = isPreviewFeatureEnabled('cost-management');
  const allowed = previewEnabled && user?.role === 'org_admin';

  const query = api.cost.getSummary.useQuery(COST_KEY, undefined, {
    queryKey: COST_KEY,
    enabled: allowed,
  });

  if (!previewEnabled || (user && !allowed)) return <Navigate to="/apps" replace />;

  const header = <PageHeader title="Cost Management" />;

  if (!user || query.isLoading) {
    return (
      <div className="space-y-6" aria-busy="true">
        {header}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-24 rounded-gl" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-gl" />
      </div>
    );
  }

  if (query.isError || query.data?.status !== 200) {
    return (
      <div className="space-y-6">
        {header}
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-3">
            <span>Could not load cost figures.</span>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}
      <CostView cost={query.data.body} />
    </div>
  );
}
